<template>
  <main class="agenda-online">
    <template v-if="ui.hasLoaded">
      <h1
        v-if="healthPlace.theme"
        class="agenda-online__title"
      >
        <img
          :src="require(`@/assets/img/health-place/${healthPlace.theme}.png`)"
          :alt="healthPlace.name"
        />
      </h1>

      <transition name="fade">
        <section
          v-if="ui.hasSaved"
          class="agenda-online__has-saved"
        >
          <h2 class="agenda-online__has-saved-tit">
            A consulta foi agendada com sucesso!
          </h2>

          <p class="agenda-online__has-saved-txt">
            O médico já foi notificado e você receberá por e-mail a
            confirmação desse agendamento, com todos os dados necessários
            para o acompanhamento.
          </p>

          <p class="agenda-online__has-saved-txt">
            Tenha um ótimo dia!
          </p>

          <a
            :href="`http://${healthPlace.url}/`"
            class="agenda-online__has-saved-link btn btn-primary"
          >
            Ir para {{ healthPlace.name }}
          </a>
        </section>
      </transition>

      <transition name="fade">
        <section
          v-if="!ui.hasSaved && ui.isRescheduling && !ui.isLoadingDoctor && rescheduling.patient"
          class="agenda-online__rescheduling"
        >
          <p class="agenda-online__rescheduling-text agenda-online__rescheduling-text--highlight">
            Olá, <strong>{{ rescheduling.patient.name }}!</strong>
          </p>

          <p class="agenda-online__rescheduling-text">
            Fazer o agendamento da sua consulta de retorno é muito fácil!
          </p>

          <p class="agenda-online__rescheduling-text">
            Basta escolher um dos horários disponíveis nos consultórios
            listados e confirmar. Tanto você como
            {{ doctor.info.user.gender === 'F' ? 'a médica' : 'o médico' }}
            receberão a confirmação através do e-mail cadastrado.
          </p>
        </section>
      </transition>

      <form
        v-if="!ui.hasSaved"
        @submit.prevent="!ui.isRescheduling ? saveAppointment() : saveRescheduling()"
      >
        <section class="agenda-online__form">
          <fieldset
            :disabled="ui.isSaving"
            class="agenda-online__section"
          >
            <h2 class="agenda-online__subtitle">
              Agenda online
            </h2>

            <section class="agenda-online__doctor">
              <dr-loader
                v-if="ui.isLoadingDoctor"
                size="lg"
                class="agenda-online__loader"
              />

              <transition name="fade">
                <div
                  v-if="!ui.isLoadingDoctor"
                  class="agenda-online__doctor-img"
                >
                  <img :src="doctor.profilePicture">
                </div>
              </transition>

              <!-- Médico não encontrado -->
              <transition name="fade">
                <div
                  v-if="!ui.isLoadingDoctor && !doctor.info"
                  class="agenda-online__doctor-info"
                >
                  <div class="agenda-online__doctor-info-col">
                    <h3 class="agenda-online__doctor-name">
                      {{ doctorName }}
                    </h3>
                  </div>
                </div>
              </transition>

              <!-- Médico encontrado -->
              <transition name="fade">
                <div
                  v-if="!ui.isLoadingDoctor && doctor.info"
                  class="agenda-online__doctor-info"
                >
                  <div class="agenda-online__doctor-info-col">
                    <h3 class="agenda-online__doctor-name">
                      {{ doctorName }}
                    </h3>

                    <h4 class="agenda-online__doctor-spec">
                      {{ medicalSpecialties }}
                    </h4>
                  </div>

                  <div class="agenda-online__doctor-info-col agenda-online__doctor-info-col--right">
                    <span
                      v-if="doctor.info['is_amb_especialization']"
                      class="agenda-online__doctor-amb"
                    >
                      Especialista AMB
                    </span>

                    <span class="agenda-online__doctor-txt">
                      CRM {{ doctor.info.crm }} / {{ doctor.info['crm_regional'] }}
                    </span>
                  </div>

                  <div class="agenda-online__doctor-info-col agenda-online__doctor-info-col--100">
                    <p class="agenda-online__doctor-txt">
                      {{ doctor.info.presentation }}
                    </p>
                  </div>
                </div>
              </transition>
            </section>

            <transition name="fade">
              <div
                v-if="doctor.offices && !healthPlace.isPartner"
                class="agenda-online__offices"
              >
                <p class="agenda-online__doctor-txt">
                  Escolha um consultório médico:
                </p>

                <ul
                  v-if="doctor.offices.length > 0"
                  class="agenda-online__offices-list"
                >
                  <li
                    v-for="
                      (office, index) in !ui.isRescheduling
                        ? doctor.offices
                        : doctor.offices
                          .filter(o =>
                            o.uuid === rescheduling.officeUuid ||
                            /telemedicine/gi.test(o.type)
                          )
                    "
                    :key="`doctor_office_${index}`"
                    :class="(chosenOffice === index) && 'agenda-online__offices-item--selected'"
                    class="agenda-online__offices-item"
                    @click="chosenOffice = index"
                  >
                    {{ office.name }}
                  </li>
                </ul>

                <transition name="fade">
                  <div
                    v-if="chosenOfficeDetails"
                    class="agenda-online__offices-info"
                  >
                    <dl class="agenda-online__offices-details">
                      <dt
                        v-if="chosenOfficeDetails.address"
                        class="agenda-online__offices-term"
                      >
                        Endereço:
                      </dt>
                      <dd
                        v-if="chosenOfficeDetails.address"
                        class="agenda-online__offices-def"
                      >
                        <p class="agenda-online__offices-address">
                          {{ chosenOfficeDetails.address.street }}, {{ chosenOfficeDetails.address.number }}
                          {{ chosenOfficeDetails.address.complement }} - {{ chosenOfficeDetails.address.district }}
                          {{ chosenOfficeDetails.address.city.name }}/{{ chosenOfficeDetails.address.uf.name }}
                          &dash;
                          {{ chosenOfficeDetails.address.cep | toCep }}
                        </p>
                      </dd>

                      <dt
                        v-if="!ui.isRescheduling"
                        class="agenda-online__offices-term"
                      >
                        Preço da consulta:
                      </dt>
                      <dd
                        v-if="!ui.isRescheduling"
                        class="agenda-online__offices-def"
                      >
                        <i
                          v-if="ui.isLoadingDoctorOfficesPrices"
                          class="icon loading"
                        />

                        <span v-else>
                          R$ {{ chosenOfficeDetails['price'] | TO_BRL }}
                        </span>
                      </dd>
                    </dl>
                  </div>
                </transition>
              </div>
            </transition>
          </fieldset>

          <transition name="fade">
            <fieldset
              v-if="chosenOfficeDetails"
              class="agenda-online__section"
            >
              <h2 class="agenda-online__subtitle">
                Data e horário
              </h2>

              <section class="agenda-online__schedule">
                <div class="agenda-online__schedule-header">
                  <p class="agenda-online__doctor-txt">
                    Escolha uma data e um horário:
                  </p>

                  <nav class="agenda-online__schedule-nav">
                    <div
                      title="Dia anterior"
                      :class="!$moment(day, 'DD/mm').isSameOrAfter($moment()) && 'disabled'"
                      class="agenda-online__schedule-nav-icon"
                      @click="
                        $moment(day).add(-1, 'days').isSameOrAfter($moment(), 'd') &&
                        navigateCalendar(-1)
                      "
                    >
                      <i class="icon icon-arrow-left" />
                    </div>

                    <datepicker
                      class="agenda-online__schedule-picker"
                      v-on:selected="handleSelectDate"
                      :disabled-dates="disabledDates"
                      :value="new Date(this.day.format())"
                      ref="programaticOpen"
                      :language="ptBR"
                    ></datepicker>

                    <span class="agenda-online__schedule-nav-day" v-on:click="openPicker()">
                      {{ weekdays[day.format('d')] }},
                      {{ day.format('DD/MM') }}
                    </span>

                    <div
                      title="Dia seguinte"
                      :class="(
                        ui.isRescheduling &&
                        $moment(day).add(1, 'days').isAfter(rescheduling.dueDate, 'day')
                      ) && 'disabled'"
                      class="agenda-online__schedule-nav-icon"
                      @click="navigateCalendar(1)"
                    >
                      <i class="icon icon-arrow-right" />
                    </div>
                  </nav>
                </div>

                <dr-loader
                  v-if="ui.isLoadingDoctorOfficesSchedule"
                  size="lg"
                  class="agenda-online__loader"
                />

                <p
                  v-if="
                    !ui.isLoadingDoctorOfficesSchedule &&
                    doctor.schedule &&
                    doctor.schedule.length === 0
                  "
                  class="agenda-online__schedule-not-found"
                >
                  Não foram encontrados horários disponíveis para este consultório
                  na data escolhida.
                </p>

                <transition name="fade">
                  <div
                    v-if="
                      !ui.isLoadingDoctorOfficesSchedule &&
                      doctor.schedule &&
                      doctor.schedule.length > 0
                    "
                    class="agenda-online__schedule-periods"
                  >
                    <div
                      v-for="(period, index) in doctor.schedule"
                      :key="`schedule-period-${index}`"
                      class="agenda-online__schedule-periods-item"
                    >
                      <span
                        :class="selectedSlot === period && 'agenda-online__schedule-periods-btn--selected'"
                        class="agenda-online__schedule-periods-btn"
                        @click="selectedSlot = period"
                      >
                        {{ period | toTime }}
                      </span>
                    </div>
                  </div>
                </transition>
              </section>
            </fieldset>
          </transition>

          <fieldset
            v-if="chosenOfficeDetails && !ui.isRescheduling"
            :disabled="ui.isSaving"
            class="agenda-online__section"
          >
            <h2 class="agenda-online__subtitle">
              Dados do paciente
            </h2>

            <div class="agenda-online__fieldset">
              <dr-field
                v-for="(field, name) in formData.specialPatient"
                :key="`onlinePatient_${name}`"
                :id="name"
                :ref="name"
                :validators.sync="$v.formData.specialPatient[name]"
                :apiErrors.sync="apiErrors[name]"
                :form.sync="formData"
                :field="field"
                prefix="onlinePatientForm_"
              />
            </div>
          </fieldset>

          <fieldset
            v-if="chosenOfficeDetails && !ui.isRescheduling"
            :disabled="ui.isSaving"
            class="agenda-online__section"
          >
            <h2 class="agenda-online__subtitle">
              Dados de endereço
            </h2>

            <div class="agenda-online__fieldset">
              <dr-field
                v-for="(field, name) in formData.address"
                :key="`onlinePatient_${name}`"
                :id="name"
                :ref="name"
                :validators.sync="$v.formData.address[name]"
                :apiErrors.sync="apiErrors[name]"
                :form.sync="formData"
                :field="field"
                prefix="onlinePatientForm_"
              />
            </div>
          </fieldset>

          <fieldset
            v-if="chosenOfficeDetails && !ui.isRescheduling"
            :disabled="ui.isSaving"
            class="agenda-online__section"
          >
            <h2 class="agenda-online__subtitle">
              Dados de pagamento
            </h2>

            <div class="agenda-online__fieldset">
              <dr-field
                v-for="(field, name) in formData.creditCard"
                :key="`onlinePatient_${name}`"
                :id="name"
                :ref="name"
                :validators.sync="$v.formData.creditCard[name]"
                :apiErrors.sync="apiErrors[name]"
                :form.sync="formData"
                :field="field"
                prefix="onlinePatientForm_"
              />
            </div>
          </fieldset>
        </section>

        <footer
          v-if="chosenOfficeDetails"
          class="agenda-online__footer"
        >
          <dr-submit
            :disabled="this.ui.isRescheduling
              ? !this.selectedSlot
              : this.$v.$invalid || !this.selectedSlot
            "
            :sending="ui.isSaving"
            :text="ui.isRescheduling ? 'Agendar retorno' : 'Agendar consulta'"
          />
        </footer>
      </form>
    </template>

    <transition
      name="fade"
      mode="in-out"
    >
      <div
        v-if="!ui.hasLoaded"
        class="spinner"
      >
        <div class="spinner__spinner" />
      </div>
    </transition>
  </main>
</template>

<script>
import { formData, validations } from '@/data/formData/onlinePatient'
import drSubmit from '@/components/form/submit'
import drField from '@/components/form/field'
import Datepicker from 'vuejs-datepicker'
import { ptBR } from 'vuejs-datepicker/dist/locale'

export default {
  name: 'AgendaOnline',

  components: {
    drSubmit,
    drField,
    Datepicker
  },

  filters: {
    toCep (cep) {
      return String(cep).replace(/(\d{5})(\d{3})/g, '$1-$2')
    },

    toTime (time) {
      return String(time).replace(/(\d{2}):(\d{2}):(\d{2})/g, '$1h$2')
    }
  },

  validations: {
    formData: validations
  },

  data () {
    return {
      formData,
      apiErrors: this.fn_BUILD_API_ERRORS(formData),
      ui: {
        isLoadingDoctor: false,
        isLoadingDoctorOffices: false,
        isLoadingDoctorOfficesPrices: false,
        isLoadingDoctorOfficesSchedule: false,
        isRescheduling: false,
        isSaving: false,
        hasSaved: false,
        hasLoaded: false
      },
      doctor: {
        uuid: this.$route.params.uuid,
        info: null,
        offices: null,
        schedule: null,
        profilePicture: require('@/assets/img/m_default.png')
      },
      rescheduling: {
        agendaUuid: null,
        doctorUuid: null,
        officeUuid: null,
        consultationFinish: null
      },
      healthPlace: {
        isPartner: true,
        theme: null,
        name: null,
        url: null,
        uuid: null
      },
      weekdays: null,
      day: this.$moment(),
      chosenOffice: null,
      selectedSlot: null,
      ptBR: ptBR,
      disabledDates: {
        to: new Date(this.$moment().format('YYYY-MM-DD'))
      }
    }
  },

  computed: {
    doctorName () {
      const name = this.doctor.info
        ? this.doctor.info['display_name']
          ? this.doctor.info['display_name']
          : this.doctor.info.user.name
        : 'Médico não encontrado'

      return name
    },

    chosenOfficeDetails () {
      if (this.chosenOffice !== null) {
        return this.doctor.offices[this.chosenOffice]
      }

      return null
    },

    isToday () {
      return this.day.isSame(this.$moment(), 'd')
    },

    medicalSpecialties () {
      if ('medical_specialties' in this.doctor.info) {
        const specialties = this.doctor.info['medical_specialties']
          .map(ms => String(ms.name)
            .toLowerCase()
            .split(' ')
            .map(w => String(w.charAt(0).toUpperCase() + w.substring(1)))
            .join(' ')
          )

        return specialties.join(', ')
      }

      return ''
    },

    agendaUuid () {
      return this.$route.query.agenda
    }
  },

  watch: {
    chosenOfficeDetails: {
      deep: true,
      immediate: false,
      handler () {
        this.getSchedule()
      }
    },

    'formData.address.cep.value': {
      deep: false,
      immediate: false,
      handler (v) {
        const regex = /([\d]){5}-([\d]){3}/g // RegEx CEP
        if (regex.test(v)) {
          this.searchAddress(v)
        }
      }
    },

    'rescheduling.officeUuid': {
      deep: false,
      immediate: false,
      handler (uuid) {
        if (uuid) {
          this.ui.isRescheduling = true
        }
      }
    },

    'formData.specialPatient.ddiContactPhone.value': {
      deep: false,
      immediate: false,
      handler (next, before) {
        if (next !== before) {
          const { mask, placeholder } = this.formData.specialPatient.ddiContactPhone.options
            .find((option) => option.id === next)

          this.formData.specialPatient.contactPhone.mask = [mask]
          this.formData.specialPatient.contactPhone.placeholder = placeholder

          if (next === 'br') {
            this.formData.specialPatient.contactPhone.mask = ['(##) ####-####', '(##) #####-####']
          }
        }
      }
    }
  },

  created () {
    this.disableHelp()

    this.$moment.locale('pt-br')

    this.weekdays = this.$moment
      .weekdays()
      .map(day => day.replace('-feira', ''))

    this.formData.specialPatient.ddiContactPhone.value = 'br'
  },

  mounted () {
    // hide zen desk
    // setTimeout(() => window.zE('webWidget', 'hide'), 5000)

    this.fn_SHOW_PROGRESS()

    this.ui.isLoadingDoctor = true
    this.ui.hasLoaded = false
    this.getDoctor()
      .then((res) => {
        this.doctor.info = res.data
        this.ui.isLoadingDoctor = false
        return true
      })
      .then(this.getDoctorProfilePicture())
      .then(() => {
        this.ui.isLoadingDoctorOffices = true
        return this.getDoctorOffices()
      })
      .then((res) => {
        this.doctor.offices = res.data.data
          .filter(
            (office) => office['app_integration'] &&
              'is_active' in office['app_integration'] &&
              office['app_integration']['is_active']
          )

        this.ui.isLoadingDoctorOffices = false

        return this.doctor.offices
          .map(office => office.uuid)
      })
      .then((offices) => {
        this.ui.isLoadingDoctorOfficesPrices = true
        return this.getDoctorOfficesPrices(offices)
      })
      .then(prices => this.setDoctorOfficesPrices(prices))
      .then(() => {
        this.getAgendaDetails(this.agendaUuid)
        this.getHealthPlaceAgendaDetails(this.agendaUuid)
          .then(this.getHealthPlacePartnerDetails)
          .then((res) => {
            this.healthPlace.isPartner = res.isPartner
            this.healthPlace.theme = res.theme
            this.healthPlace.name = res.name
            this.healthPlace.url = res.url
            this.healthPlace.uuid = res.uuid

            if (this.healthPlace.isPartner) {
              this.chosenOffice = this.doctor.offices
                .findIndex((office) => office.type === 'TELEMEDICINE')
            }
          })
      })
      .catch((err) => this.fn_HANDLE_ERROR(err))
      .finally(() => {
        this.fn_HIDE_PROGRESS()
        this.ui.isLoadingDoctor = false
        this.ui.isLoadingDoctorOffices = false
        this.ui.isLoadingDoctorOfficesPrices = false
        this.ui.hasLoaded = true
      })
  },

  methods: {
    getDoctor () {
      const url = `/external/doctor/${this.doctor.uuid}`

      return this.$http
        .get(url)
    },

    getDoctorOffices () {
      const url = `/external/doctor/${this.doctor.uuid}/doctor_office`

      return this.$http
        .get(url)
    },

    getDoctorOfficesPrices (ids) {
      const getters = []

      ids.forEach((id) => {
        const url = `/external/doctor/${this.doctor.uuid}/doctor_office/${id}/specialty_price`
        const get = this.$http
          .get(url)
          .then((res) => ({ uuid: id, price: res.data }))

        getters.push(get)
      })

      return new Promise((resolve, reject) => {
        Promise.all(getters).then(res => resolve(res))
      })
    },

    setDoctorOfficesPrices (offices) {
      offices.forEach((office) => {
        const officeObj = this.doctor.offices.find(o => o.uuid === office.uuid)
        officeObj['price'] = office.price
      })
    },

    getDoctorProfilePicture () {
      const url = `/s3/file/doctor/${this.doctor.uuid}/profile_picture`

      return this.$http
        .get(url)
        .then(() => (this.doctor.profilePicture = `${process.env.ROOT_API}${url}`))
        .catch(() => {
          if (this.doctor.uuid) {
            this.doctor.profilePicture = this.doctor.info.user.gender === 'F'
              ? require('@/assets/img/f_default.png')
              : require('@/assets/img/m_default.png')
          }
        })
    },

    getSchedule () {
      const initialDate = this.day.format('YYYY-MM-DD')
      const finalDate = this.$moment(this.day).format('YYYY-MM-DD')
      const endpoint = `/external/doctor/${
        this.doctor.info.uuid
      }/doctor_office/${
        this.chosenOfficeDetails.uuid
      }/agenda_available?initialDate=${
        initialDate
      }&finalDate=${
        finalDate
      }`

      this.selectedSlot = null
      this.doctor.schedule = []

      this.fn_SHOW_PROGRESS()
      this.ui.isLoadingDoctorOfficesSchedule = true

      this.$http
        .get(endpoint)
        .then((res) => {
          this.doctor.schedule = Object.keys(res.data)[0]
            ? [...res.data[Object.keys(res.data)[0]]]
            : []

          // remove periods <= now
          if (this.isToday && this.doctor.schedule) {
            this.doctor.schedule = this.doctor.schedule
              .filter((period) => {
                const a = parseInt(period.replace(':', '').slice(0, -2))
                const b = parseInt(this.$moment().format('HHmm'))

                return b <= a
              })
          }

          // remove periods <= now + 2h for facetoface offices
          if (
            this.isToday &&
            this.doctor.schedule &&
            /facetoface/gi.test(this.chosenOfficeDetails.type)
          ) {
            this.doctor.schedule = this.doctor.schedule
              .filter((period) => {
                const a = parseInt(period.replace(':', '').slice(0, -2))
                const b = parseInt(this.$moment().add(2, 'h').format('HHmm'))

                return b < a
              })
          }

          // remove periods <= final time for rescheduling
          if (
            this.ui.isRescheduling &&
            this.day.isSame(this.rescheduling.dueDate, 'day')
          ) {
            this.doctor.schedule = this.doctor.schedule
              .filter((period) => {
                const a = parseInt(period.replace(':', '').slice(0, -2))
                const b = parseInt(this.rescheduling.dueDate.format('HHmm'))

                return a < b
              })
          }
        }
        )
        .catch(err => console.log(err))
        .finally(() => {
          this.fn_HIDE_PROGRESS()
          this.ui.isLoadingDoctorOfficesSchedule = false
        })
    },

    navigateCalendar (delta) {
      this.day.add(delta, 'days')
      this.getSchedule()
    },

    openPicker () {
      this.$refs.programaticOpen.showCalendar()
    },

    handleSelectDate (dt) {
      this.day = this.$moment(dt)
      this.getSchedule()
    },

    saveAppointment () {
      const url = '/external/special_patient/agenda'
      const method = 'post'
      const data = this.buildApiObject()

      this.ui.isSaving = true
      this.fn_SHOW_PROGRESS()
      this.$http({ url, method, data })
        .then(res => (this.ui.hasSaved = true))
        .catch(err => this.fn_HANDLE_ERROR(err))
        .finally(() => {
          this.ui.isSaving = false
          this.fn_HIDE_PROGRESS()
        })
    },

    saveRescheduling () {
      const url = this.healthPlace.isPartner
        ? `/health_place_partner/${this.healthPlace.uuid}/agenda/${this.rescheduling.agendaUuid}/return`
        : `/external/special_patient/agenda/${this.rescheduling.agendaUuid}/return`

      const appointmentStart = `${this.day.format('YYYY-MM-DD')} ${this.selectedSlot}`
      const appointmentFinish = this.$moment(this.getAppointmentFinish(appointmentStart))
        .format('YYYY-MM-DD HH:mm:ss')
      const doctorOffice = { uuid: this.chosenOfficeDetails.uuid }

      const form = this.healthPlace.isPartner
        ? 'health_place_agenda_return'
        : 'external_agenda_return'

      const data = {
        [form]: {
          agenda: {
            appointmentStart,
            appointmentFinish,
            doctorOffice
          }
        }
      }

      this.ui.isSaving = true
      this.fn_SHOW_PROGRESS()
      this.$http
        .post(url, data)
        .then(res => (this.ui.hasSaved = true))
        .catch(err => this.fn_HANDLE_ERROR(err))
        .finally(() => {
          this.ui.isSaving = false
          this.fn_HIDE_PROGRESS()
        })
    },

    buildApiObject () {
      const key = 'external_agenda'
      const appointmentStart = `${this.day.format('YYYY-MM-DD')} ${this.selectedSlot}`
      const appointmentFinish = this.$moment(this.getAppointmentFinish(appointmentStart))
        .format('YYYY-MM-DD HH:mm:ss')

      const agenda = {
        appointmentStart,
        appointmentFinish,
        // installments: this.formData.creditCard.installments.value,
        installments: 1,
        doctor: { uuid: this.doctor.info.uuid },
        doctorOffice: { uuid: this.chosenOfficeDetails.uuid }
      }

      const specialPatient = {}
      for (let [key, value] of Object.entries(this.formData.specialPatient)) {
        specialPatient[key] = value.value
      }
      specialPatient.cpf = specialPatient.cpf.replace(/\D/gi, '')
      specialPatient.contactPhone = specialPatient.contactPhone.replace(/\D/gi, '')
      specialPatient.dateOfBirth = this.$moment(specialPatient.dateOfBirth, 'DD/MM/YYYY', true)
        .format('YYYY-MM-DD')

      const creditCard = {}
      for (let [key, value] of Object.entries(this.formData.creditCard)) {
        if (key !== 'installments') {
          creditCard[key] = value.value
        }
      }
      creditCard.number = creditCard.number.replace(/\s/gi, '')
      creditCard.expirationDate = creditCard.expirationDate.replace('/', '')

      specialPatient.creditCard = creditCard

      const address = {}
      for (let [key, value] of Object.entries(this.formData.address)) {
        address[key] = value.value
      }

      specialPatient.address = address
      specialPatient.address.cep = specialPatient.address.cep.replace(/\D/gi, '')
      delete specialPatient.address.latitude
      delete specialPatient.address.longitude

      const ufName = specialPatient.address.uf
      const cityName = specialPatient.address.city

      delete specialPatient.address.city
      delete specialPatient.address.uf

      specialPatient.address['city'] = { name: cityName }
      specialPatient.address['uf'] = { name: ufName }

      const paymentMethod = 'CARTÃO DE CRÉDITO'

      specialPatient.ddiContactPhone = this.formData.specialPatient.ddiContactPhone.options
        .find((option) => option.id === specialPatient.ddiContactPhone)
        .code

      return {
        [key]: {
          agenda,
          specialPatient,
          paymentMethod
        }
      }
    },

    getAppointmentFinish (start) {
      const timeStart = this.$moment('1970-01-01T00:00:00-03:00')
      const hours = this.$moment(this.chosenOfficeDetails['appointment_interval'])
        .diff(timeStart, 'h')
      const minutes = this.$moment(this.chosenOfficeDetails['appointment_interval'])
        .diff(timeStart, 'm')

      return this.$moment(start)
        .add(hours, 'h')
        .add(minutes, 'm')
    },

    searchAddress (cep) {
      this.$http
        .get(`https://viacep.com.br/ws/${cep.replace('-', '')}/json/`)
        .then((res) => {
          if (res.data) {
            const { data } = res
            const setDisable = (object, property, value) => {
              property.forEach(p => (object[p].disabled = value))
            }

            setDisable(
              this.formData.address,
              ['street', 'district', 'city', 'uf', 'street'],
              false
            )
            // se encontrou o CEP, mas sem rua e bairro, habilita estes campos para edição
            if (!data.logradouro || !data.bairro || (data.logradouro.length + data.bairro.length) === 0) {
              setDisable(
                this.formData.address,
                ['district', 'street'],
                false
              )
            }

            this.formData.address.street.value = data.logradouro
            this.formData.address.district.value = data.bairro
            this.formData.address.city.value = data.localidade
            this.formData.address.uf.value = data.uf

            if ('erro' in data) {
              this.$store.dispatch('setModalConfirm', {
                title: 'CEP não encontrado',
                body: 'Deseja pesquisar um outro CEP ou editar o endereço manualmente?',
                confirmText: 'Editar',
                cancelText: 'Pesquisar',
                beforeClose: (v) => {
                  if (v) {
                    setDisable(
                      this.formData.address,
                      ['street', 'district', 'city', 'uf'],
                      false
                    )
                  }
                  this.$store.dispatch('setModalConfirm')
                },
                size: 'md',
                active: true
              })
            } else {
              const key = this.$store.getters.$_getMapsApi
              const address = `${this.formData.address.street.value},
                ${this.formData.address.number.value},
                ${this.formData.address.district.value},
                ${this.formData.address.city.value} -
                ${this.formData.address.uf.value}`

              this.$http
                .get(`https://maps.google.com/maps/api/geocode/json?key=${key}&address=${address}`)
                .then((res) => {
                  if (res.status === 200) {
                    const { data } = res
                    if (data.results.length > 0) {
                      const { geometry } = data.results[0]
                      this.formData.address.latitude.value = geometry.location.lat
                      this.formData.address.longitude.value = geometry.location.lng
                    }
                  }
                })
                .catch((err) => {
                  console.log('err', err)
                })
            }
          }
        })
        .catch((err) => {
          console.log('err', err)
        })
    },

    getAgendaDetails (uuid = null) {
      if (uuid) {
        this.fn_SHOW_PROGRESS()
        this.$http
          .get(`/external/agenda/${uuid}`)
          .then((res) => {
            const { data } = res
            this.rescheduling.doctorUuid = data['doctor']['uuid']
            this.rescheduling.officeUuid = data['doctor_office']['uuid']
            this.rescheduling.agendaUuid = this.agendaUuid
            this.rescheduling.patient = data['special_patient']

            this.rescheduling.consultationFinish = this.$moment(data['medical_consultation_finish'])
            this.rescheduling.dueDate = this.$moment(this.rescheduling.consultationFinish).add(30, 'days')
          })
          .catch((err) => this.fn_HANDLE_ERROR(err))
          .finally(() => {
            this.fn_HIDE_PROGRESS()
          })
      }
    },

    getHealthPlaceAgendaDetails (uuid = null) {
      return new Promise((resolve) => {
        if (uuid) {
          this.$http
            .get(`/health_place_partner/agenda/${uuid}`)
            .then((res) => {
              this.rescheduling.patient = res.data.user
              resolve(res.data['health_place_partner']['access_url'])
            })
            .catch(resolve)

          return
        }

        resolve()
      })
    },

    getHealthPlacePartnerDetails (accessUrl = null) {
      const defaultPartner = {
        isPartner: false,
        theme: 'drconecta',
        name: 'Dr. Conecta',
        url: 'drconecta.com.br',
        uuid: null
      }

      return new Promise((resolve) => {
        if (accessUrl) {
          this.$http
            .get(`/health_place_partner/access_url/${accessUrl}`)
            .then((res) => {
              const partner = {
                isPartner: true,
                theme: res.data['template_style_guide'],
                name: res.data.name,
                url: accessUrl,
                uuid: res.data.uuid
              }

              resolve(partner)
            })
            .catch(() => resolve(defaultPartner))

          return
        }

        resolve(defaultPartner)
      })
    },

    disableHelp () {
      const observer = new MutationObserver(() => {
        const iframe = document.querySelector('#omnizeButtonFrame') || null

        if (iframe) iframe.style.display = 'none'
      })

      observer.observe(document.body, { childList: true, subtree: true })
    }
  }
}
</script>
